import { useParams } from "react-router";
import { IngredientsList } from "@/features/IngredientsList/IngredientsList";
import { DirectionsList } from "@/features/DirectionsList/DirectionsList";
import { useGetRecipeImage } from "@/hooks/useGetRecipeImage";
import { useGetRecipe } from "@/hooks/useGetRecipe";
import { RecipeDetails } from "@/features/RecipeDetails/RecipeDetails";
import { RecipeDetailsSkeleton } from "@/features/RecipeDetails/RecipeDetailsSkeleton";
import { RecipeSourceChip } from "@/components/RecipeSourceChip";
import { FavoriteToggle, MadeItChip, RecipeCategoryChip, RecipeTypeChip } from "@/components/chips";
import { HeroPageLayout } from "@/layouts/HeroPageLayout";
import { MarkAsMadePanel } from "@/features/MarkAsMadePanel";
import { RecipeLog } from "@/features/RecipeLog/RecipeLog";
import { SectionDivider } from "@/components/SectionDivider";

export const Recipe2 = () => {
    const { recipeId } = useParams();
    const { data: recipe, isPending, isError } = useGetRecipe(Number(recipeId));
    const { imageUrl } = useGetRecipeImage(recipe?.image);

    if (isPending) {
        return <RecipeDetailsSkeleton />;
    }

    if (!recipe || isError) {
        return <div>Error or Recipe not found.</div>;
    }

    return (
        <HeroPageLayout
            coverImageUrl={imageUrl || ""}
            headerContent={
                <>
                    <p className="font-display text-sm tracking-[0.2em] text-[var(--fg-muted)] uppercase mb-2">
                        {recipe.recipeType.name}
                    </p>
                    <h1 className="font-serif text-4xl xl:text-6xl xl:max-w-[575px]">{recipe.name}</h1>
                    <div className="flex flex-wrap gap-2 mt-[30px] mb-[20px] max-w-[575px] items-center">
                        <FavoriteToggle recipeId={recipe.id} favorite={recipe.favorite} hasMadeIt={recipe.hasMadeIt} />
                        <MadeItChip state={recipe.hasMadeIt ? "made" : "not-tried"} />
                        <RecipeTypeChip name={recipe.recipeType.name} />
                        {recipe.categories.map((category) => (
                            <RecipeCategoryChip key={category.id} name={category.name} />
                        ))}
                        {recipe.sourceUrl && <RecipeSourceChip sourceUrl={recipe.sourceUrl} />}
                    </div>
                    <p className="text-lg/6 py-4 xl:mb-8 max-w-[575px] xl:max-h-[150px] overflow-y-auto">
                        {recipe.description && <span>{recipe.description}</span>}
                    </p>
                </>
            }
        >
            <RecipeDetails recipe={recipe} />
            <div className="flex flex-col mx-auto w-full max-w-7xl overflow-x-hidden sm:overflow-x-visible">
                <div className="flex row flex-wrap lg:flex-nowrap w-full gap-4 lg:gap-16">
                    <div className="col-lg-4 w-full lg:basis-2/5 lg:pl-12">
                        <div className="rounded-xl bg-default-100 border border-default-200 h-full relative px-6 pt-8 pb-6">
                            <section className="sticky top-24">
                                <IngredientsList ingredients={recipe.ingredientGroups} />
                            </section>
                        </div>
                    </div>
                    <div className="relative col-lg-5 w-full lg:basis-3/5 lg:pr-12">
                        <div className="h-full relative px-4 py-4 lg:pt-8 lg:pb-0">
                            <section className="sticky top-24 mb-6">
                                <DirectionsList
                                    directions={recipe.directions}
                                    notes={recipe.notes}
                                    storage={recipe.storage}
                                    afterDirections={<MarkAsMadePanel recipeId={recipe.id} />}
                                />
                            </section>
                        </div>
                    </div>
                </div>
                <SectionDivider />
                <RecipeLog recipeId={recipe.id} />
            </div>
        </HeroPageLayout>
    );
};
